<template>
    <div :style="{ height: videoHeightStyle }" class="d-flex flex-column">
        <div :id="videoIdSanitized" class="w-100 flex-grow-1 embedded-video"></div>
    </div>
</template>

<script>
import YouTubePlayer from "youtube-player"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "EmbeddedVideo",
    props: {
        videoUrl: {
            type: String,
            required: false,
        },
        height: {
            type: Number,
            default: 320,
        },
        fullHeight: {
            type: Boolean,
            default: false,
        },
        fullScreen: {
            type: Boolean,
            default: false
        },
        autoplay: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            player: null,
            videoIdSanitized: null,
            startSeconds: 0,
        }
    },
    computed: {
        videoHeightStyle() {
            return this.fullHeight ? "100%" : this.height + "px"
        },
    },
    methods: {
        // Initialize the embedded YouTube video.
        initializeEmdeddedYT() {
            let parsed = this.parseYouTubeUrl(this.videoUrl)
            if (parsed) {
                this.videoIdSanitized = parsed.videoId
                this.startSeconds = parsed.startSeconds
            }
            let vm = this
            // Wait for the element to be rendered before initializing the player.
            this.$nextTick(() => vm.initYTPlayer())
        },
        initYTPlayer() {
            // Initialize the player.
            this.player = YouTubePlayer(this.videoIdSanitized, {
                videoId: this.videoIdSanitized,
                playerVars: {
                    autoplay: 0,
                    controls: 1,
                    rel: 0,
                    fs: 1,
                    color: "white",
                    modestbranding: 1,
                    origin: window.location.origin,
                    playsinline: 1,
                },
            })

            // Determine which function to call, depending on autoplay settings. The loadVideoById
            // function causes the video to start playing immediately.
            let playerFunction = this.autoplay ? this.player.loadVideoById : this.player.cueVideoById
            playerFunction({
                videoId: this.videoIdSanitized,
                startSeconds: this.startSeconds
            })

            let vm = this
            this.player.on("ready", (event) => {
                // Enter full screen if flag is set.
                if (vm.fullScreen) {
                    let player = document.getElementById(this.videoIdSanitized)
                    Utilities.enterFullScreen(player)
                }
            })

            this.player.on("stateChange", (event) => {
                if (event.data === 0) {
                    this.$emit("video-finished", true)
                    this.videoComplete = true
                    // Exit full screen.
                    if (vm.fullScreen) {
                        Utilities.exitFullScreen()
                    }
                }
            })
        },
        // Restart the video.
        restart() {
            this.$emit("video-restart")
            this.player.playVideo()
        },
        // Extract the video ID and start time (in seconds) from a YouTube URL.
        parseYouTubeUrl(input) {
            const raw = String(input || "").trim()
            if (!raw) return null

            // h:m:s or 1h2m3s or plain seconds -> seconds
            function timeToSeconds(val) {
                if (!val) return undefined
                const t = String(val).replace(/^#?t=/i, "").trim()

                // "90"
                if (/^\d+$/.test(t)) return parseInt(t, 10)
                // "1:30" or "1:02:03"
                const colon = t.match(/^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/)
                if (colon) {
                    const h = colon[3] != null ? parseInt(colon[1], 10) : 0
                    const m = colon[3] != null ? parseInt(colon[2], 10) : parseInt(colon[1], 10)
                    const s = colon[3] != null ? parseInt(colon[3], 10) : parseInt(colon[2], 10)
                    return h * 3600 + m * 60 + s
                }
                const hms = t.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i)
                // "1h2m3s"
                if (hms) {
                    const hh = hms[1] ? parseInt(hms[1], 10) : 0
                    const mm = hms[2] ? parseInt(hms[2], 10) : 0
                    const ss = hms[3] ? parseInt(hms[3], 10) : 0
                    return hh * 3600 + mm * 60 + ss
                }
                return undefined;
            }

            const ID_RE = /^[a-zA-Z0-9_-]{11}$/

            // Accept bare 11-char IDs
            if (ID_RE.test(raw)) return { videoId: raw }

            // Build URL (add protocol if missing)
            let url;
            try {
                url = new URL(raw)
            } catch {
                try {
                    url = new URL("https://" + raw)
                } catch { return null }
            }

            // Unwrap older "attribution_link" share URLs (u=/watch%3Fv%3D...)
            if (url.pathname === "/attribution_link" && (url.searchParams.get("u") || url.searchParams.get("q"))) {
                const inner = url.searchParams.get("u") || url.searchParams.get("q")
                try {
                    const unescaped = decodeURIComponent(inner)
                    url = new URL(unescaped.startsWith("http") ? unescaped : "https://youtube.com" + unescaped)
                } catch {
                    /* ignore */
                }
            }

            const host = url.hostname.replace(/^www\./, "")
            let videoId = null

            const isYouTubeHost =
                /(^|\.)youtube\.com$/.test(host) ||
                /(^|\.)youtube-nocookie\.com$/.test(host) ||
                host === "youtu.be" || host === "m.youtube.com" || host === "music.youtube.com"

            if (!isYouTubeHost) return null

            // youtu.be/<id>
            if (host === "youtu.be") {
                const id = url.pathname.split("/")[1] || "";
                if (ID_RE.test(id)) videoId = id
            }

            if (!videoId && host !== "youtu.be") {
                const parts = url.pathname.split("/").filter(Boolean)

                // Clips are not regular videos (cannot be embedded via videoId alone)
                if (parts[0] === "clip") {
                    return { error: "clips_not_supported" }
                }

                // /watch?v=<id>
                if (url.pathname === "/watch") {
                    const v = url.searchParams.get("v")
                    if (v && ID_RE.test(v)) videoId = v
                }

                // /shorts/<id>
                if (!videoId && parts[0] === "shorts" && parts[1] && ID_RE.test(parts[1])) {
                    videoId = parts[1]
                }

                // /embed/<id>
                if (!videoId && parts[0] === "embed" && parts[1] && ID_RE.test(parts[1])) {
                    videoId = parts[1]
                }

                // /live/<id>
                if (!videoId && parts[0] === "live" && parts[1] && ID_RE.test(parts[1])) {
                    videoId = parts[1]
                }

                // legacy /v/<id>
                if (!videoId && parts[0] === "v" && parts[1] && ID_RE.test(parts[1])) {
                    videoId = parts[1]
                }
            }

            if (!videoId) return null;

            // Timestamp: ?t=, ?start=, ?time_continue=, hash (#t= or #1:30)
            const startSeconds =
                timeToSeconds(url.searchParams.get("t")) ??
                timeToSeconds(url.searchParams.get("start")) ??
                timeToSeconds(url.searchParams.get("time_continue")) ??
                timeToSeconds(url.hash)

            return { videoId, startSeconds }
        },
        skipVideo() {
            let vm = this
            this.player.getDuration().then(function (duration) {
                vm.player.seekTo(duration, true)
            })
        },
    },
    watch: {
        videoId() {
            this.$nextTick(function () {
                this.initializeEmdeddedYT()
            })
        },
    },
    mounted() {
        this.initializeEmdeddedYT()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.embedded-video {
    overflow: hidden;
}
</style>